import { joiResolver } from '@hookform/resolvers/joi';
import {
  Box,
  Dialog,
  DialogContent,
  DialogInProgress,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from 'app/design';
import {
  RemoveCircle as RemoveIcon,
  AddCircleOutlined as AddIcon,
} from 'app/design/icons-material';
import { DefaultDialogActions } from 'app/components/DefaultDialogActions';
import { HookFormTextField } from 'app/components/reactHookFormComponents/HookFormTextField';
import {
  useCreateContact,
  useUpdateContactPartial,
} from 'app/hooks/mutations/contact';
import { getAtPath } from 'app/utilities';
import useDialogBuilder from 'app/utilities/useDialog';
import { startCase } from 'lodash';
import InputMask from 'react-input-mask';
import { TextField as InputTextField } from 'app/design';

import Joi from 'joi';
import { errors } from 'msw/lib/types/context';
import React, { useEffect } from 'react';
import {
  Controller,
  FieldPath,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { toast } from 'react-toastify';
import { Contact } from 'types/contact';
import { v4 as uuidv4 } from 'uuid';

interface EditContactDialogProps {
  contact?: Contact;
  open: boolean;
  onCancel: () => void;
  onComplete: () => void;
  initialNumber?: string;
}

interface Form {}

const phoneNumbersSchema = Joi.array()
  .items(
    Joi.object({
      id: Joi.string().required(),
      countryCode: Joi.string().required(),
      // TODO: https://www.npmjs.com/package/joi-phone-number when we allow different country codes
      number: Joi.string().allow(''),
      // .required()
      // .label('Number')
      // .regex(/[_]/, { invert: true }),
      digits: Joi.string().allow(null, ''), // parsed from number so not required, set during saving
      label: Joi.string().allow(null, '').label('Label'),
    }),
  )
  // .min(1)
  .label('Phone Numbers');

const firstNameSchema = Joi.string().max(128).label('First Name');
const lastNameSchema = Joi.string().max(128).label('First Name');
const companySchema = Joi.string().max(128).label('First Name');

const schema = Joi.object({
  // couldn't get this alternatives to work with just info({...}).or('lastName', etc...) due to conflict with needing to allow('')
  info: Joi.alternatives().try(
    Joi.object({
      firstName: firstNameSchema.allow(''),
      lastName: lastNameSchema.allow(''),
      company: companySchema,
      phoneNumbers: phoneNumbersSchema,
    }),
    Joi.object({
      firstName: Joi.string().max(128).label('First Name').allow(''),
      lastName: lastNameSchema,
      company: companySchema.allow(''),
      phoneNumbers: phoneNumbersSchema,
    }),
    Joi.object({
      firstName: Joi.string().max(128).label('First Name'),
      lastName: lastNameSchema.allow(''),
      company: companySchema.allow(''),
      phoneNumbers: phoneNumbersSchema,
    }),
  ),
});

const EditContactDialog = ({
  contact,
  open,
  onCancel,
  onComplete,
  initialNumber,
}: EditContactDialogProps) => {
  const createContactMutation = useCreateContact();
  const updateContactMutation = useUpdateContactPartial();
  const formMethods = useForm({
    defaultValues: contact
      ? { info: contact.info }
      : {
          info: {
            phoneNumbers: [
              {
                id: uuidv4(),
                countryCode: 'us',
                number: initialNumber ?? '',
                digits: initialNumber ?? '',
                label: 'mobile',
              },
            ],
          },
        },
    resolver: joiResolver(schema),
  });

  const handleSave = async ({
    info: { phoneNumbers, ...infoRest },
    ...rest
  }) => {
    const numbers: any[] = [];

    phoneNumbers.forEach(n => {
      // const num = n.number.replace(/(\+1 )/g, '');
      //
      console.log('n', n);
      if (!n.number) {
        return;
      }

      numbers.push({
        ...n,
        digits: n.number,
        // number: num,
        // digits: num.replace(/([() -])/g, ''),
      });
    });

    const data = {
      ...rest,
      info: {
        ...(infoRest as Contact['info']),
        phoneNumbers: numbers,
      },
      source: null,
      schema: 'v0.1' as const,
    };

    // console.log('data', { ...contact, ...data });

    if (contact) {
      const updateContactPromise = updateContactMutation.mutateAsync({
        ...contact,
        ...data,
      });

      // toast.promise(updateContactPromise, {
      //   pending: 'Updating contact...',
      //   success: 'Contact updated!',
      //   error: 'Error updating contact.',
      // });

      let result = await updateContactPromise;
      onComplete();
    } else {
      const createContactPromise = createContactMutation.mutateAsync(data, {
        // onSuccess: onComplete,
      });

      // toast.promise(createContactPromise, {
      //   pending: 'Creating contact...',
      //   success: 'Contact added!',
      //   error: 'Error creating contact.',
      // });

      let result = await createContactPromise;
      onComplete();
    }
  };

  // console.log('saving errors', formMethods.formState.errors);
  // error thrown for not having at least one of the following fields: 'firstName', 'lastName', 'company'
  const requiredFieldError =
    // @ts-ignore
    formMethods.formState.errors?.info?.type === 'alternatives.match';

  useEffect(() => {
    const fields = [
      'info.firstName',
      'info.lastName',
      'info.company',
    ] as FieldPath<Pick<Contact, 'info'>>[];
    if (requiredFieldError) {
      fields.forEach(field => {
        formMethods.setError(field, {
          message: 'Contact requires at least one of these fields.',
        });
      });
    } else {
      formMethods.clearErrors(fields);
    }
  }, [requiredFieldError]);

  return (
    // @ts-ignore
    <Dialog open={open} fullWidth maxWidth={'sm'}>
      {updateContactMutation.isLoading || createContactMutation.isLoading ? (
        <DialogInProgress
          title={
            updateContactMutation.isLoading
              ? 'Updating contact...'
              : 'Creating contact...'
          }
        />
      ) : (
        <FormProvider {...formMethods}>
          <DialogTitle>{`${contact ? 'Edit' : 'New'} Contact`}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} alignItems={'center'}>
              <Grid item xs={6}>
                <HookFormTextField
                  fullWidth
                  name={'info.firstName'}
                  label={'First Name'}
                  autoFocus={!contact}
                />
              </Grid>
              <Grid item xs={6}>
                <HookFormTextField
                  fullWidth
                  name={'info.lastName'}
                  label={'Last Name'}
                />
              </Grid>
              <Grid item xs={8}>
                <HookFormTextField
                  fullWidth
                  name={'info.company'}
                  label={'Company'}
                />
              </Grid>
            </Grid>

            <HookFormPhoneNumbers name={'info.phoneNumbers'} />
          </DialogContent>
          <DefaultDialogActions
            onSave={formMethods.handleSubmit(handleSave)}
            saveDisabled={!formMethods.formState.isDirty}
            saveLabel={
              formMethods.formState.isDirty
                ? `${contact ? 'Update' : 'Create'} Contact`
                : 'No changes'
            }
            onCancel={onCancel}
          />
        </FormProvider>
      )}
    </Dialog>
  );
};

const HookFormPhoneNumbers = ({ name }) => {
  const {
    control,
    formState: { errors: formErrors },
  } = useFormContext();

  const { append, remove, fields } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name, // unique name for your Field Array
    // shouldUnregister: true,
  });

  const handleRemove = (index: number) => () => {
    remove(index);
  };

  const handleAdd = () => {
    append({
      id: uuidv4(),
      countryCode: 'us',
      number: '',
      digits: '',
      label: '',
    });
  };

  return (
    <>
      <br />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Typography variant={'h3'}>Numbers:</Typography>
          {formErrors?.info?.phoneNumbers?.message ? (
            <Typography variant={'caption'} color={'error'}>
              {formErrors.info.phoneNumbers.message}
            </Typography>
          ) : null}
        </div>
        <IconButton onClick={handleAdd}>
          <AddIcon color={'success'} />
        </IconButton>
      </Box>
      <div style={{ padding: 5 }}>
        {fields.map((field, index) => (
          <Grid
            sx={{ mt: 2 }}
            container
            alignItems={'center'}
            columnSpacing={2}
          >
            <Grid item xs={5}>
              <HookFormTextField
                fullWidth
                size={'small'}
                name={`${name}.${index}.label`}
                label={'Label'}
              />
            </Grid>
            <Grid item xs={6}>
              <HookFormTextField
                size={'small'}
                name={`${name}.${index}.number`}
              />
              {/*<HookFormPhoneNumber*/}
              {/*  name={`${name}.${index}.number`}*/}
              {/*  errorMessageParser={error => 'Invalid number.'}*/}
              {/*/>*/}
            </Grid>
            <Grid item xs={1}>
              {fields.length > 1 ? (
                <IconButton onClick={handleRemove(index)}>
                  <RemoveIcon color={'error'} />
                </IconButton>
              ) : null}
            </Grid>
          </Grid>
        ))}
      </div>
    </>
  );
};

const HookFormPhoneNumber = ({
  name,
  errorMessageParser,
}: {
  name: string;
  errorMessageParser?: (error: any) => string;
}) => {
  const {
    control,
    formState: { errors: formErrors },
  } = useFormContext();

  const error = getAtPath(formErrors, name);

  const errorMessage = error
    ? errorMessageParser
      ? errorMessageParser(error)
      : error.message
    : undefined;
  return (
    <Controller
      // @ts-ignore
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur, ...props } }) => (
        <InputMask
          mask="+1 (999) 999-9999"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        >
          <InputTextField
            // @ts-ignore
            {...props}
            // @ts-ignore
            label="Number"
            size="small"
            fullWidth
            error={!!errorMessage}
            helperText={errorMessage?.replace(`"${name}"`, startCase(name))}
          />
        </InputMask>
      )}
    />
  );
};

export const useEditContactDialog = (initialOpen = false) => {
  const useDialog = useDialogBuilder(EditContactDialog);
  return useDialog({ initialOpen });
};

export default EditContactDialog;
